import { calculateDistance } from '@/utils/distanceCalculation';
import { cachedAPICall, getFromCache, CACHE_KEYS, CACHE_TTL } from '@/utils/cache';
import { RELATED_INTERESTS } from '@/constants/interests';
import { getChildAge } from '@/utils/ageCalculation';

/**
 * Perfect Match Service
 * Provides algorithmic camp matching based on user profile and preferences
 */

/**
 * Mapping from preferred_camp_types (short values) to primary_category (full names)
 * This ensures accurate matching between user preferences and camp categories
 */
const PREFERRED_TYPE_TO_CATEGORY_MAP = {
  'sports': ['Sports & Fitness'],
  'arts': ['Arts & Culture'],
  'stem': ['Science & Technology'],
  'outdoor': ['Outdoor & Adventure'],
  'academic': ['Academic & Learning'],
  'specialty': ['Specialty Programs']
};

/**
 * Determine camp type (day vs overnight)
 * @param {Object} camp - Camp object
 * @returns {string} - 'day' or 'overnight'
 */
function getCampType(camp) {
  // Check camp_type from camp_audit_data table (primary source)
  // Handle 'both' first - camps that offer both day and overnight programs
  if (camp.camp_type === 'both') {
    return 'both';
  }
  
  // Values like 'overnight_camp' or 'overnight' indicate overnight camps
  if (camp.camp_type === 'overnight_camp' || camp.camp_type === 'overnight') {
    return 'overnight';
  }
  
  // Legacy support: Check camp duration/preference fields
  if (camp.duration === 'overnight' || 
      camp.camp_duration_preference === 'overnight' ||
      camp.camp_duration === 'overnight') {
    return 'overnight';
  }
  
  // Default to day camp
  return 'day';
}

/**
 * Get relevant preferences based on camp type
 * @param {Object} userProfile - User profile with family_preferences
 * @param {Object} camp - Camp object
 * @returns {Object} - Relevant preference object
 */
function getRelevantPreferences(userProfile, camp) {
  const campType = getCampType(camp);
  const prefs = userProfile.family_preferences || userProfile.preferences || {};
  let relevantPrefs;
  
  // Check if user has type-specific preferences enabled
  if (prefs.preferences_by_type) {
    // For 'both' camps, use the preference with larger max_distance (more generous)
    if (campType === 'both') {
      const dayPrefs = prefs.day_camp || {};
      const overnightPrefs = prefs.overnight_camp || {};
      
      // Use preference with larger max_distance (more generous)
      const dayDistance = dayPrefs.max_distance_miles || 25; // default for day
      const overnightDistance = overnightPrefs.max_distance_miles || 800; // default for overnight
      
      // If overnight preference exists and has larger/equal distance, use it
      // Otherwise use day preference, or general if neither exists
      if (Object.keys(overnightPrefs).length > 0 && overnightDistance >= dayDistance) {
        relevantPrefs = overnightPrefs;
      } else if (Object.keys(dayPrefs).length > 0) {
        relevantPrefs = dayPrefs;
      } else {
        relevantPrefs = prefs.general || prefs;
      }
    } else {
      relevantPrefs = campType === 'overnight' 
      ? (prefs.overnight_camp || prefs.general || prefs)
      : (prefs.day_camp || prefs.general || prefs);
  }
  } else {
  // Fallback to general preferences or legacy top-level preferences
    relevantPrefs = prefs.general || prefs;
  }
  
  // For overnight camps, default max_distance_miles to 800 if not set in preferences
  if (campType === 'overnight' && !relevantPrefs.max_distance_miles) {
    // Create a new object to avoid mutating the original
    relevantPrefs = { ...relevantPrefs, max_distance_miles: 800 };
  }
  
  return relevantPrefs;
}

/**
 * Calculate age matching score (0-5 points)
 * Drastically reduced weight since most camps don't have age data
 * Returns both score and per-child age data
 */
function calculateAgeScore(campAgeMin, campAgeMax, children, verbose = false) {
  if (!children || children.length === 0) {
    if (verbose) console.log('  ⚠️ [Age Score] No children, returning 0');
    return { score: 0, childAgeScores: [] };
  }
  
  let totalScore = 0;
  let validChildren = 0;
  const childAgeScores = [];
  
  children.forEach(child => {
    const childAge = getChildAge(child);
    if (childAge === null) return;
    
    const campMin = parseInt(campAgeMin) || 0;
    const campMax = parseInt(campAgeMax) || 18;
    
    let childScore = 0;
    // Perfect match: child age is within camp range
    if (childAge >= campMin && childAge <= campMax) {
      childScore = 5; // Minimal points for perfect age match
    }
    // Close match: child is 1 year outside range
    else if (childAge >= campMin - 1 && childAge <= campMax + 1) {
      childScore = 3;
    }
    // Partial match: child is 2 years outside range
    else if (childAge >= campMin - 2 && childAge <= campMax + 2) {
      childScore = 2;
    }
    // Poor match: child is more than 2 years outside range
    else {
      childScore = 1;
    }
    
    totalScore += childScore;
    validChildren++;
    
    // Track per-child age score
    childAgeScores.push({
      childId: child.id || child.name,
      childName: child.name,
      age: childAge,
      ageScore: childScore,
      ageMatch: childAge >= campMin && childAge <= campMax
    });
    
    if (verbose) console.log(`  ✓ [Age Score] Child age: ${childAge}, Camp range: ${campMin}-${campMax}, Score: ${childScore.toFixed(1)}`);
  });
  
  // Return average score per child, capped at 5
  const finalScore = validChildren > 0 ? Math.min(totalScore / validChildren, 5) : 0;
  if (verbose) console.log(`  ✓ [Age Score] Final: ${finalScore.toFixed(1)} (avg of ${validChildren} children)`);
  return { score: finalScore, childAgeScores };
}

/**
 * Check if interest has related matches in camp tags
 * Uses RELATED_INTERESTS mapping from constants for better matching
 */
function hasRelatedMatch(interest, campTags) {
  // First check the RELATED_INTERESTS mapping
  const relatedInterests = RELATED_INTERESTS[interest] || [];
  
  // Check if any related interest appears in camp tags
  const hasRelatedInterestMatch = relatedInterests.some(relatedInterest => {
    const relatedLower = relatedInterest.toLowerCase();
    return campTags.some(tag => {
      const tagLower = String(tag).toLowerCase();
      return tagLower.includes(relatedLower) || relatedLower.includes(tagLower);
    });
  });
  
  if (hasRelatedInterestMatch) {
    return true;
  }
  
  // Fallback: Check for generic related terms (for interests not in mapping)
  const interestLower = interest.toLowerCase();
  const genericRelatedTerms = {
    'sports': ['athletics', 'fitness', 'physical', 'exercise'],
    'arts': ['creative', 'visual', 'crafts', 'artistic'],
    'science': ['stem', 'experiments', 'discovery', 'research'],
    'technology': ['tech', 'computer', 'digital', 'software'],
    'outdoor': ['nature', 'adventure', 'wilderness', 'environment'],
    'academic': ['learning', 'education', 'studies', 'scholastic']
  };
  
  // Check if interest category matches
  for (const [category, terms] of Object.entries(genericRelatedTerms)) {
    if (campTags.some(tag => {
      const tagLower = String(tag).toLowerCase();
      return terms.some(term => tagLower.includes(term) || term.includes(tagLower));
    })) {
      // Check if the interest itself relates to this category
      if (interestLower.includes(category) || category.includes(interestLower)) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Calculate interest matching score (0-35 points)
 * Improved to better differentiate specific vs generic matches
 */
function calculateInterestScore(camp, children, verbose = false) {
  // Backward compatibility: if first arg is string, treat as old signature
  if (typeof camp === 'string' || !camp || typeof camp !== 'object') {
    // Old signature: (campCategory, campTags, children, verbose)
    const campCategory = camp;
    const campTags = children;
    const actualChildren = arguments[2];
    const actualVerbose = arguments[3];
    // Create minimal camp object for backward compatibility
    return calculateInterestScore(
      { category: campCategory, tags: campTags || [] },
      actualChildren,
      actualVerbose
    );
  }

  if (!children || children.length === 0) {
    if (verbose) console.log('  ⚠️ [Interest Score] No children, returning 0');
    return { score: 0, matchedInterests: [], hasExactMatch: false, categoryMatch: false, exactMatchCount: 0, relatedMatchCount: 0, breakdown: { category: 0, specific: 0, bonus: 0 }, matchedChildren: [] };
  }
  
  let totalScore = 0;
  let validChildren = 0;
  let allMatchedInterests = [];
  let hasAnyExactMatch = false;
  let hasAnyCategoryMatch = false;
  let totalExactMatches = 0;
  let totalCategoryScore = 0;
  let totalSpecificScore = 0;
  let totalBonusScore = 0;
  let totalRelatedMatches = 0;
  const matchedChildrenData = []; // Track per-child match data
  
  // Helper: Normalize activities from comma-separated string to array
  // Our extraction script stores activities as "pottery, coding, soccer" 
  // but the code expects an array. This normalizes both formats.
  const normalizeActivities = (activities) => {
    if (!activities) return [];
    if (Array.isArray(activities)) return activities.map(a => String(a).toLowerCase().trim());
    // Handle comma-separated string from extraction
    return String(activities)
      .split(',')
      .map(a => a.trim().toLowerCase())
      .filter(Boolean);
  };
  
  const campActivitiesArray = normalizeActivities(camp.activities);
  
  // Enhanced debug logging for interest matching
  if (verbose || import.meta.env.DEV) {
    console.log(`🔍 [Interest Matching] Starting match for camp: ${camp.name || 'Unknown'}`, {
      campActivitiesRaw: camp.activities,
      campActivitiesNormalized: campActivitiesArray,
      campActivitiesCount: campActivitiesArray.length,
      childrenCount: children.length,
      childrenInterests: children.map(c => ({
        name: c.name,
        interests: c.interests || [],
        interestsCount: (c.interests || []).length,
        interestsFormat: (c.interests || []).map(i => ({ original: i, lowercased: String(i).toLowerCase() }))
      }))
    });
  }
  
  // Determine camp type once (used for overnight camp adjustments)
  const campType = getCampType(camp);
  const isOvernightCamp = campType === 'overnight';
  
  // Get all camp tags/activities (normalize to lowercase)
  const campTags = [
    ...(camp.tags || []),
    ...campActivitiesArray,  // Use normalized array instead of raw activities
    ...(camp.all_categories || []),
    camp.name,
    camp.category,
    camp.primary_category
  ]
    .filter(Boolean)
    .map(t => String(t).toLowerCase());
  
  children.forEach(child => {
    // Check both interests and preferred_camp_types
    const childInterests = child.interests || [];
    const preferredTypes = child.preferred_camp_types || [];
    
    if (childInterests.length === 0 && preferredTypes.length === 0) {
      if (verbose) console.log(`  ⚠️ [Interest Score] Child has no interests or preferred types`, { child: { name: child.name } });
      return;
    }
    
    let childScore = 0;
    const matchedInterests = [];
    let hasExactMatch = false;
    let categoryMatch = false;
    
    // 1. Category Match (15 points)
    // For overnight camps, apply more generous category matching since they often have less detailed data
    // REMOVED preferredTypes requirement - allow category matching based on camp's category alone
    // This gives interests more impact and doesn't require preferred_camp_types to be set
    if (camp.category || camp.primary_category) {
      const categoryLower = (camp.category || '').toLowerCase();
      const primaryCategoryLower = (camp.primary_category || '').toLowerCase();
      
      // Check if camp category matches preferred camp types (if user has set preferences)
      if (preferredTypes.length > 0) {
        // PRIORITY 1: Use explicit mapping for accurate matching
        categoryMatch = preferredTypes.some(type => {
          const typeLower = String(type).toLowerCase();
          const mappedCategories = PREFERRED_TYPE_TO_CATEGORY_MAP[typeLower] || [];
          
          // Check against primary_category using explicit mapping
          if (camp.primary_category) {
            const primaryMatch = mappedCategories.some(mappedCat => 
              primaryCategoryLower === mappedCat.toLowerCase()
            );
            if (primaryMatch) {
              if (verbose) console.log(`  ✓ [Interest Score] Primary category match (exact): "${camp.primary_category}" matches preferred type "${type}"`);
              return true;
            }
          }
          
          // Check against camp.category using explicit mapping
          if (camp.category) {
            const categoryMatchFound = mappedCategories.some(mappedCat => 
              categoryLower === mappedCat.toLowerCase()
            );
            if (categoryMatchFound) {
              if (verbose) console.log(`  ✓ [Interest Score] Category match (exact): "${camp.category}" matches preferred type "${type}"`);
              return true;
            }
          }
          
          // For overnight camps: More generous matching - check if category name contains keywords
          // This helps when camp categories are close but not exact (e.g., "Outdoor Adventure" vs "outdoor")
          if (isOvernightCamp && (camp.primary_category || camp.category)) {
            const campCategoryText = (camp.primary_category || camp.category).toLowerCase();
            // Check if type name appears in category (e.g., "outdoor" in "Outdoor & Adventure")
            if (campCategoryText.includes(typeLower) || typeLower.includes(campCategoryText.split(' ')[0])) {
              if (verbose) console.log(`  ✓ [Interest Score] Overnight camp category match (generous): "${camp.primary_category || camp.category}" matches preferred type "${type}"`);
              return true;
            }
          }
          
          return false;
        });
      }
      
      // For overnight camps: If no preferredTypes match but camp has a category, give partial credit
      // This recognizes that overnight camps in categories like "Outdoor & Adventure" are valuable even without exact matches
      if (isOvernightCamp && !categoryMatch && (camp.primary_category || camp.category)) {
        // Only apply if child has any preferred types (shows intent) but doesn't match exactly
        if (preferredTypes.length > 0) {
          categoryMatch = true; // More generous: any category for overnight camps if child has preferences
          if (verbose) console.log(`  ✓ [Interest Score] Overnight camp category bonus: "${camp.primary_category || camp.category}" (generous match)`);
        }
      }
      
      if (categoryMatch) {
        childScore += 15; // Increased weight for interest matching
        totalCategoryScore += 15;
        hasAnyCategoryMatch = true;
        const matchedCategory = camp.primary_category || camp.category;
        if (verbose) console.log(`  ✓ [Interest Score] Category match: "${matchedCategory}" (+15 points)`);
      }
    }
    
    // 2. Specific Interest Matches (up to 40 points)
    // Process each child interest with precise taxonomy matching
    let specificScore = 0;
    const exactMatches = [];
    const relatedMatches = [];
    
    childInterests.forEach(interest => {
      const interestLower = String(interest).toLowerCase().trim();
      
      // PRIORITY 1: Exact taxonomy match in camp activities
      // This is case-insensitive exact match (e.g., "basketball" === "Basketball")
      const exactMatch = campActivitiesArray.includes(interestLower);

      if (exactMatch) {
        // Exact match: 30 points per interest
        specificScore += 30;
        exactMatches.push(interest);
        matchedInterests.push(interest);
        hasExactMatch = true;
        hasAnyExactMatch = true;
        totalExactMatches++;
        if (verbose) {
          console.log(`  ✓ [Interest] Exact taxonomy match: "${interest}" (+30 pts)`);
        }
      } else {
        // PRIORITY 2: Related match using RELATED_INTERESTS mapping
        // Case-insensitive lookup - find the key that matches regardless of case
        // Child interests are stored as lowercase in database, RELATED_INTERESTS keys are capitalized
        const interestKey = Object.keys(RELATED_INTERESTS).find(
          key => key.toLowerCase() === interestLower
        );
        
        let foundRelatedMatch = false;
        
        if (interestKey) {
          const relatedInterests = RELATED_INTERESTS[interestKey] || [];
          const relatedFound = relatedInterests.filter(related => 
            campActivitiesArray.includes(related.toLowerCase())
          );
          
          if (relatedFound.length > 0) {
            // Related match: 20 points per related interest found
            const relatedPoints = 20 * relatedFound.length;
            specificScore += relatedPoints;
            relatedMatches.push(...relatedFound);
            // Push original child interest, not the related interest found
            // This ensures display shows the child's interest (e.g., "Coding") not the camp's activity (e.g., "Robotics")
            matchedInterests.push(interest);
            totalRelatedMatches += relatedFound.length;
            foundRelatedMatch = true;
            if (verbose) {
              console.log(`  ✓ [Interest] Related match: "${interest}" → [${relatedFound.join(', ')}] (+${relatedPoints} pts)`);
            }
          } else {
            if (verbose) {
              console.log(`  ⚠️ [Interest] Found RELATED_INTERESTS key "${interestKey}" but no related activities matched in camp`);
            }
          }
        } else {
          if (verbose) {
            console.log(`  ⚠️ [Interest] No RELATED_INTERESTS key found for "${interest}" (lowercase: "${interestLower}")`);
          }
        }
        
        // PRIORITY 3: Substring match fallback (backward compatibility)
        // Only try if RELATED_INTERESTS didn't find a match
        // For camps without extracted activities or legacy matching
        if (!foundRelatedMatch) {
          const substringMatch = campTags.some(tag => 
            tag.includes(interestLower) || interestLower.includes(tag)
          );
          
          if (substringMatch) {
            specificScore += 4; // Lower score for fuzzy match
            relatedMatches.push(interest);
            matchedInterests.push(interest);
            totalRelatedMatches++;
            if (verbose) {
              console.log(`  ✓ [Interest] Substring match (fallback): "${interest}" (+4 pts)`);
            }
          } else {
            if (verbose) {
              console.log(`  ✗ [Interest] No match: "${interest}"`);
            }
          }
        }
      }
    });

    // Cap specific score at 60 points per child (increased to accommodate higher point values)
    specificScore = Math.min(specificScore, 60);
    
    if (verbose && specificScore > 0) {
      console.log(`  → Specific interest score: ${specificScore}/60 pts (${exactMatches.length} exact, ${relatedMatches.length} related)`);
    }
    childScore += specificScore;
    totalSpecificScore += specificScore;

    // 3. Multi-Interest Bonus (0-5 points)
    const uniqueExactMatches = exactMatches.length;
    let bonusScore = 0;

    if (uniqueExactMatches >= 3) {
      bonusScore = 5;
      childScore += 5;
      totalBonusScore += 5;
      if (verbose) console.log(`  ✓ [Interest Score] Multi-interest bonus (${uniqueExactMatches} matches): +5 points`);
    } else if (uniqueExactMatches >= 2) {
      bonusScore = 3;
      childScore += 3;
      totalBonusScore += 3;
      if (verbose) console.log(`  ✓ [Interest Score] Multi-interest bonus (${uniqueExactMatches} matches): +3 points`);
    }

    // Cap total at 80 points per child (15 category + 60 specific + 5 bonus)
    childScore = Math.min(childScore, 80);

    if (childScore > 0) {
      totalScore += childScore;
      validChildren++;
      allMatchedInterests.push(...matchedInterests);
      totalRelatedMatches += relatedMatches.length;
      
      // Track per-child match data
      const childMatchData = {
        childId: child.id || child.name, // Use ID if available, fallback to name
        childName: child.name,
        score: childScore,
        matchedInterests: [...new Set(matchedInterests)], // Remove duplicates
        exactMatchCount: exactMatches.length,
        relatedMatchCount: relatedMatches.length,
        hasExactMatch: hasExactMatch,
        categoryMatch: categoryMatch
      };
      matchedChildrenData.push(childMatchData);
      
      if (verbose) console.log(`  ✓ [Interest Score] Child score: ${childScore}`, { childName: child.name, interests: childInterests, exactMatches, relatedMatches });
    }
  });
  
  // Return average score per child, capped at 80
  let finalScore = validChildren > 0 ? Math.min(totalScore / validChildren, 80) : 0;
  
  // Overnight camp baseline boost: Give overnight camps a baseline score if they have low/no interest matches
  // This helps them reach the threshold since they often don't have detailed activity data
  // (campType is already defined above before the children.forEach loop)
  if (isOvernightCamp && finalScore < 15) {
    const baselineBoost = 20; // Baseline boost for overnight camps
    finalScore = Math.min(finalScore + baselineBoost, 60);
    if (verbose) {
      console.log(`  ✓ [Interest Score] Overnight camp baseline boost: +${baselineBoost} points (final: ${finalScore.toFixed(1)})`);
    }
  }
  
  // Calculate average breakdown
  const avgBreakdown = validChildren > 0 ? {
    category: Math.round(totalCategoryScore / validChildren),
    specific: Math.round(totalSpecificScore / validChildren),
    bonus: Math.round(totalBonusScore / validChildren)
  } : { category: 0, specific: 0, bonus: 0 };
  
  const finalMatchedInterests = [...new Set(allMatchedInterests)]; // Remove duplicates
  
  if (verbose || import.meta.env.DEV) {
    console.log(`  ✓ [Interest Score] Final: ${finalScore.toFixed(1)}/80 (avg of ${validChildren} children with interests)`);
    console.log(`  ✓ [Interest Score] Breakdown:`, avgBreakdown);
    console.log(`  ✓ [Interest Score] Matched Interests:`, {
      count: finalMatchedInterests.length,
      interests: finalMatchedInterests,
      exactMatches: totalExactMatches,
      relatedMatches: totalRelatedMatches,
      hasExactMatch: hasAnyExactMatch,
      categoryMatch: hasAnyCategoryMatch
    });
  }
  
  return {
    score: Math.round(finalScore),
    matchedInterests: finalMatchedInterests,
    hasExactMatch: hasAnyExactMatch,
    categoryMatch: hasAnyCategoryMatch,
    exactMatchCount: totalExactMatches,
    relatedMatchCount: totalRelatedMatches,
    breakdown: avgBreakdown,
    matchedChildren: matchedChildrenData // Per-child match data
  };
}

/**
 * Helper: Convert time string to minutes since midnight
 */
function timeToMinutes(timeStr) {
  if (!timeStr) return 0;
  
  // Handle various formats: "9:00 AM", "09:00", "9am", "9:00am"
  const cleanTime = timeStr.trim().toLowerCase().replace(/\s+/g, '');
  
  let hours = 0;
  let minutes = 0;
  let isPM = cleanTime.includes('pm');
  
  // Extract numbers
  const match = cleanTime.match(/(\d{1,2}):?(\d{2})?/);
  if (match) {
    hours = parseInt(match[1]);
    minutes = match[2] ? parseInt(match[2]) : 0;
    
    // Convert to 24-hour format
    if (isPM && hours < 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;
  }
  
  return hours * 60 + minutes;
}

/**
 * Enhanced schedule scoring using community times
 */
function calculateScheduleScore(camp, familyPreferences, verbose = false) {
  const campType = getCampType(camp);
  
  // Overnight camps get a higher baseline schedule score since schedule preferences are less relevant
  // This helps them reach the threshold since they can't earn schedule bonuses like day camps
  // 'Both' camps should use normal day camp schedule scoring (not the overnight baseline)
  if (campType === 'overnight') {
    if (verbose) console.log('  ✓ [Schedule Score] Overnight camp - baseline score applied');
    return { score: 18, reason: 'Overnight camp', confidence: 'none' }; // Higher baseline for overnight camps
  }
  
  // Use community-contributed session times if available
  const sessionStart = camp.user_session_start_time;
  const sessionEnd = camp.user_session_end_time;
  
  if (!sessionStart || !sessionEnd || !familyPreferences?.preferred_times) {
    return { 
      score: 10, // Neutral score when missing
      reason: null, // Don't show "No schedule data available" - only show when there's actual data
      confidence: 'none'
    };
  }
  
  const preferredDropOff = familyPreferences.preferred_times.drop_off;
  const preferredPickUp = familyPreferences.preferred_times.pick_up;
  
  if (!preferredDropOff || !preferredPickUp) {
    return {
      score: 10, // Neutral score when camp has schedule but user hasn't specified preferences
      reason: `Camp hours: ${sessionStart} - ${sessionEnd}`, // Show actual schedule data
      confidence: camp.schedule_confidence || 'medium'
    };
  }
  
  // Calculate time difference in minutes
  const dropOffDiff = Math.abs(
    timeToMinutes(sessionStart) - timeToMinutes(preferredDropOff)
  );
  const pickUpDiff = Math.abs(
    timeToMinutes(sessionEnd) - timeToMinutes(preferredPickUp)
  );
  
  const totalDiff = dropOffDiff + pickUpDiff;
  
  // Normalize to 0-20 point scale (matching distance score range)
  let score = 20;
  if (totalDiff < 30) score = 20; // Within 30 minutes = perfect
  else if (totalDiff < 60) score = 18; // Within 1 hour = great
  else if (totalDiff < 120) score = 15; // Within 2 hours = good
  else if (totalDiff < 180) score = 11; // Within 3 hours = workable
  else score = 7;
  
  // Boost for community verification
  if (camp.schedule_confidence === 'high' || camp.verification_count > 2) {
    score = Math.min(score + 1, 20);
  }
  
  if (verbose) {
    console.log(`  ✓ [Schedule Score] Camp: ${sessionStart} - ${sessionEnd}, Preferred: ${preferredDropOff} - ${preferredPickUp}, Diff: ${totalDiff}min, Score: ${score}`);
  }
  
  return {
    score,
    reason: totalDiff < 60 ? 'Perfect schedule fit' : 'Workable schedule',
    sessionTime: `${sessionStart} - ${sessionEnd}`,
    confidence: camp.schedule_confidence || 'medium',
    communityVerified: camp.community_contribution_count > 0
  };
}

/**
 * Calculate distance score (0-20 points)
 * Higher score for closer camps
 * Now uses max_distance_miles from preferences when available
 */
function calculateDistanceScore(campLocation, userLocations, userSearchLocation = null, preferences = null, verbose = false) {
  if (!campLocation) {
    if (verbose) console.log('  ⚠️ [Distance Score] No campLocation, returning 0');
    return 0;
  }
  
  // Normalize camp location coordinates - handle both {lat, lng} and {latitude, longitude} formats
  const campLat = campLocation?.lat ?? campLocation?.latitude;
  const campLng = campLocation?.lng ?? campLocation?.longitude;
  
  // Check for valid numeric coordinates (not NaN, null, undefined, or 0)
  const isValidLat = campLat != null && !isNaN(campLat) && campLat !== 0;
  const isValidLng = campLng != null && !isNaN(campLng) && campLng !== 0;
  
  if (!isValidLat || !isValidLng) {
    if (verbose) console.log('  ⚠️ [Distance Score] Camp location missing coordinates, returning default 12', {
      campLocation,
      campLat,
      campLng,
      isValidLat,
      isValidLng
    });
    return 12; // Slightly positive neutral
  }
  
  // Priority: home address > search location > default score
  const locations = [];
  
  if (userLocations?.home) {
    const homeLat = userLocations.home.lat ?? userLocations.home.latitude;
    const homeLng = userLocations.home.lng ?? userLocations.home.longitude;
    if (homeLat && homeLng) {
      locations.push({ 
        latitude: homeLat, 
        longitude: homeLng, 
        priority: 1 
      });
    }
  }
  
  if (userSearchLocation) {
    const searchLat = userSearchLocation.lat ?? userSearchLocation.latitude;
    const searchLng = userSearchLocation.lng ?? userSearchLocation.longitude;
    if (searchLat && searchLng) {
      locations.push({ 
        latitude: searchLat, 
        longitude: searchLng, 
        priority: 2 
      });
    }
  }
  
  if (locations.length === 0) {
    if (verbose) console.log('  ⚠️ [Distance Score] No user locations with valid coordinates, returning default 12', {
      hasHomeLocation: !!userLocations?.home,
      hasSearchLocation: !!userSearchLocation
    });
    return 12; // Slightly positive neutral
  }
  
  // Calculate distance to closest location
  let minDistance = Infinity;
  
  locations.forEach(location => {
    if (location.latitude && location.longitude) {
      const distance = calculateDistance(
        location.latitude,
        location.longitude,
        campLat,
        campLng
      );
      minDistance = Math.min(minDistance, distance);
    }
  });
  
  if (minDistance === Infinity) {
    if (verbose) console.log('  ⚠️ [Distance Score] Could not calculate distance, returning default 12', {
      campLocation: { lat: campLat, lng: campLng },
      userLocations,
      userSearchLocation,
      locations
    });
    return 12; // Slightly positive neutral
  }
  
  // Get max distance from preferences, default to 25 miles if not set
  const maxDistance = preferences?.max_distance_miles || 25;
  
  // Score based on distance relative to user's max distance preference
  let score;
  const distanceRatio = minDistance / maxDistance;
  
  if (distanceRatio <= 0.33) score = 20;      // Very close (within 1/3 of max)
  else if (distanceRatio <= 0.5) score = 18;   // Close (within 1/2 of max)
  else if (distanceRatio <= 0.67) score = 15;  // Reasonable (within 2/3 of max)
  else if (distanceRatio <= 0.83) score = 12; // Acceptable (within 5/6 of max)
  else if (distanceRatio <= 1.0) score = 8;    // At max distance
  else if (distanceRatio <= 1.5) score = 5;   // Over max but within 1.5x
  else score = 2;                               // Way over max distance

  if (verbose) console.log(`  ✓ [Distance Score] Distance: ${minDistance.toFixed(1)}mi, Max: ${maxDistance}mi, Ratio: ${distanceRatio.toFixed(2)}, Score: ${score}`);
  return score;
}

/**
 * Enhanced price scoring using community cost data
 */
function calculatePriceScore(camp, familyBudget, verbose = false) {
  // Handle backward compatibility: if first arg is not a camp object, use old signature
  let campPrice = null;
  let budgetPreferences = null;
  
  if (typeof camp === 'object' && camp !== null) {
    // New signature: (camp, familyBudget, verbose)
    budgetPreferences = familyBudget;
    familyBudget = budgetPreferences?.total_summer_budget 
      ? budgetPreferences.total_summer_budget / 8 // Weekly budget
      : null;
  } else {
    // Old signature: (campPrice, budgetPreferences, verbose)
    campPrice = camp;
    budgetPreferences = familyBudget;
    familyBudget = budgetPreferences?.total_summer_budget 
      ? budgetPreferences.total_summer_budget / 8 // Weekly budget
      : null;
  }
  
  // Prioritize user-contributed cost data (more accurate)
  let campCost = null;
  let costConfidence = 'low';
  
  if (typeof camp === 'object' && camp !== null) {
    if (camp.user_cost_details?.amount) {
      campCost = camp.user_cost_details.amount;
      costConfidence = camp.cost_confidence || 'medium';
    } else if (camp.cost) {
      campCost = camp.cost;
      costConfidence = 'low'; // Google Places cost estimates are often inaccurate
    }
  } else if (campPrice) {
    // Old signature fallback
    campCost = parseFloat(campPrice.toString().replace(/[^0-9.]/g, ''));
    costConfidence = 'low';
  }
  
  if (!campCost || !familyBudget) {
    if (verbose) console.log('  ⚠️ [Price Score] No cost data available');
    return { 
      score: 10, // Neutral score when missing
      reason: 'No cost data available',
      confidence: 'none'
    };
  }
  
  const difference = Math.abs(campCost - familyBudget);
  const percentDiff = difference / familyBudget;
  
  // Normalize to 0-20 point scale (matching distance score range)
  let score = 20;
  if (percentDiff < 0.1) score = 20; // Within 10%
  else if (percentDiff < 0.25) score = 17; // Within 25%
  else if (percentDiff < 0.5) score = 13; // Within 50%
  else if (percentDiff < 0.75) score = 9; // Within 75%
  else score = 5;
  
  // Boost confidence for community-verified data
  if (typeof camp === 'object' && camp !== null) {
    if (costConfidence === 'high' || camp.verification_count > 2) {
      score = Math.min(score + 1, 20);
    }
  }
  
  if (verbose) {
    console.log(`  ✓ [Price Score] Cost: $${campCost}, Budget: $${familyBudget.toFixed(2)}, Diff: ${percentDiff.toFixed(2)}, Score: ${score}`);
  }
  
  return {
    score,
    reason: campCost <= familyBudget ? 'Within budget' : `$${Math.round(difference)} over budget`,
    confidence: costConfidence,
    amount: campCost,
    communityVerified: typeof camp === 'object' && camp !== null && camp.community_contribution_count > 0
  };
}

/**
 * Enhanced date availability scoring
 */
function calculateDateScore(camp, childAvailability, verbose = false) {
  const campStartDate = camp.user_camp_start_date || camp.start_date;
  const campEndDate = camp.user_camp_end_date || camp.end_date;
  
  if (!campStartDate || !campEndDate || !childAvailability?.available_dates) {
    return {
      score: 10, // Neutral score when missing
      reason: 'No date information',
      confidence: 'none'
    };
  }
  
  const availStart = new Date(childAvailability.available_dates.start);
  const availEnd = new Date(childAvailability.available_dates.end);
  const campStart = new Date(campStartDate);
  const campEnd = new Date(campEndDate);
  
  // Check overlap
  const hasOverlap = campStart <= availEnd && campEnd >= availStart;
  
  if (!hasOverlap) {
    return {
      score: 0,
      reason: 'Dates don\'t match availability',
      confidence: camp.dates_confidence || 'medium'
    };
  }
  
  // Calculate percentage of camp that falls within availability
  const overlapStart = new Date(Math.max(campStart, availStart));
  const overlapEnd = new Date(Math.min(campEnd, availEnd));
  const overlapDays = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24);
  const campDays = (campEnd - campStart) / (1000 * 60 * 60 * 24);
  const overlapPercent = overlapDays / campDays;
  
  // Normalize to 0-20 point scale (matching distance score range)
  let score = Math.round(overlapPercent * 20);
  
  // Boost for community-verified dates
  if (camp.dates_confidence === 'high' || camp.verification_count > 1) {
    score = Math.min(score + 1, 20);
  }
  
  if (verbose) {
    console.log(`  ✓ [Date Score] Camp: ${campStartDate} - ${campEndDate}, Available: ${childAvailability.available_dates.start} - ${childAvailability.available_dates.end}, Overlap: ${Math.round(overlapPercent * 100)}%, Score: ${score}`);
  }
  
  return {
    score,
    reason: overlapPercent === 1 ? 'Perfect date match' : `${Math.round(overlapPercent * 100)}% overlap`,
    confidence: camp.dates_confidence || 'medium',
    communityVerified: camp.community_contribution_count > 0
  };
}

/**
 * Calculate overall data confidence and boost final score
 * Base score can be up to ~152 points (35+35+20+20+20+20+2), but we normalize against
 * the actually available maximum to avoid penalizing missing data.
 */
function calculateFinalScore(baseScore, camp, availableMax = 152) {
  // Normalize base score to 0-100 scale using available factors only
  const maxPossibleBaseScore = availableMax > 0 ? availableMax : 152;
  let finalScore = (baseScore / maxPossibleBaseScore) * 100;
  
  // Boost for community enhancements
  if (camp.has_community_enhancements) {
    const communityBoost = Math.min(
      camp.community_contribution_count * 0.5, // 0.5 points per contribution
      5 // Max 5 point boost
    );
    finalScore += communityBoost;
  }
  
  // Boost for verification
  if (camp.verification_count > 0) {
    const verificationBoost = Math.min(
      camp.verification_count * 1, // 1 point per verification
      3 // Max 3 point boost
    );
    finalScore += verificationBoost;
  }
  
  // Apply gentler data confidence multiplier (90% - ~102%)
  const confidence = camp.data_confidence_score || 50;
  const confidenceMultiplier = 0.90 + (confidence / 100) * 0.12; // 90-102% of score
  finalScore = finalScore * confidenceMultiplier;
  
  // Ensure score stays within 0-100
  return Math.max(0, Math.min(Math.round(finalScore), 100));
}

/**
 * Generate match reason based on scoring factors
 * Enhanced with specific, personalized details including interests, distance, age, and preferences
 */
function generateMatchReason(camp, userProfile, scores) {
  const reasons = [];
  
  // 1. Interest-based reasons (always show when available - prioritize this)
  let hasInterestMatch = false;
  
  // Enhanced debug logging for match reason generation
  if (import.meta.env.DEV) {
    console.log(`🔍 [Match Reason] Generating reasons for ${camp.name}:`, {
      hasInterestScore: !!scores.interest,
      interestScore: scores.interestScore,
      hasMatchedInterests: !!scores.interest?.matchedInterests,
      matchedInterestsCount: scores.interest?.matchedInterests?.length || 0,
      matchedInterests: scores.interest?.matchedInterests || [],
      exactMatchCount: scores.interest?.exactMatchCount || 0,
      relatedMatchCount: scores.interest?.relatedMatchCount || 0,
      hasExactMatch: scores.interest?.hasExactMatch || false,
      categoryMatch: scores.interest?.categoryMatch || false
    });
  }
  
  // 1. Interest-based reasons (separated by child)
  if (scores.interest && scores.interest.matchedChildren && scores.interest.matchedChildren.length > 0) {
    // Group interests by child
    const children = userProfile.children || [];
    const childrenMap = new Map(children.map(c => [c.id || c.name, c]));
    
    scores.interest.matchedChildren.forEach(childMatch => {
      const childId = childMatch.childId;
      const child = childrenMap.get(childId);
      const childName = child?.name || childMatch.childName || 'your child';
      
      if (childMatch.matchedInterests && childMatch.matchedInterests.length > 0) {
        // Capitalize first letter of each interest
        const formattedInterests = [...new Set(childMatch.matchedInterests)].slice(0, 4).map(interest => {
          return interest.split(' ').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join(' ');
        });
        
        // Create child-specific interest reason
        reasons.push(`Matches ${childName}'s interest${formattedInterests.length > 1 ? 's' : ''}: ${formattedInterests.join(', ')}`);
        hasInterestMatch = true;
      }
    });
  } else if (scores.interest && scores.interest.matchedInterests && scores.interest.matchedInterests.length > 0) {
    // Fallback: if matchedChildren not available, use matchedInterests (backward compatibility)
    const matchedInterests = [...new Set(scores.interest.matchedInterests)]; // Remove duplicates
    
    if (matchedInterests.length > 0) {
      // Capitalize first letter of each interest
      const formattedInterests = matchedInterests.slice(0, 4).map(interest => {
        return interest.split(' ').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
      });
    
      // Always show interests when we have matched interests
      reasons.push(`Matches your child's interest${matchedInterests.length > 1 ? 's' : ''}: ${formattedInterests.join(', ')}`);
      hasInterestMatch = true;
    }
  } else {
    // Debug: log when interests exist but aren't being added
    if (scores.interest) {
      console.log(`⚠️ [Match Reason] Interests exist but not added for ${camp.name}:`, {
        hasInterest: !!scores.interest,
        hasMatchedInterests: !!scores.interest.matchedInterests,
        matchedInterestsLength: scores.interest.matchedInterests?.length || 0,
        interestScore: scores.interestScore
      });
    }
  }
  
  // 2. Preferred camp type reasons (with specific category names)
  // Only show if no interest matches were found (interests are more personalized)
  if (!hasInterestMatch && scores.interest && scores.interest.categoryMatch) {
    const preferredTypes = userProfile.children?.flatMap(c => c.preferred_camp_types || []).filter(Boolean) || [];
    const matchedTypes = [];
    
    // Check which preferred types match the camp's primary_category or category
    preferredTypes.forEach(type => {
      const typeLower = String(type).toLowerCase();
      const mappedCategories = PREFERRED_TYPE_TO_CATEGORY_MAP[typeLower] || [];
      const campCategoryLower = (camp.primary_category || camp.category || '').toLowerCase();
      
      if (mappedCategories.some(cat => campCategoryLower === cat.toLowerCase())) {
        matchedTypes.push(mappedCategories[0]); // Use the display name
      }
    });
    
    // Deduplicate matchedTypes to avoid showing same category twice
    const uniqueMatchedTypes = [...new Set(matchedTypes)];
    
    // Only show "preferred camp type" reason if user has explicitly set preferences
    // This prevents showing category matches when user hasn't set preferred_camp_types
    if (uniqueMatchedTypes.length > 0) {
      reasons.push(`Matches your preferred camp type: ${uniqueMatchedTypes.join(', ')}`);
    }
  }
  
  // 3. Distance-based reasons (with actual distance calculation)
  if (scores.distanceScore > 0 && camp.location && userProfile.locations?.home) {
    const campLat = camp.location.lat ?? camp.location.latitude;
    const campLng = camp.location.lng ?? camp.location.longitude;
    const homeLat = userProfile.locations.home.lat ?? userProfile.locations.home.latitude;
    const homeLng = userProfile.locations.home.lng ?? userProfile.locations.home.longitude;
    
    if (campLat && campLng && homeLat && homeLng && !isNaN(campLat) && !isNaN(campLng) && !isNaN(homeLat) && !isNaN(homeLng)) {
      const distance = calculateDistance(homeLat, homeLng, campLat, campLng);
      if (distance < 100) { // Only show if reasonable distance
  if (scores.distanceScore >= 18) {
          reasons.push(`${distance.toFixed(1)} miles from your home`);
        } else if (scores.distanceScore >= 15) {
          reasons.push(`${distance.toFixed(1)} miles from your home`);
        } else if (scores.distanceScore >= 10) {
          reasons.push(`${distance.toFixed(1)} miles away`);
        }
      }
    }
  } else if (scores.distanceScore >= 18) {
    // Fallback if distance can't be calculated
    reasons.push(`Conveniently located near you`);
  } else if (scores.distanceScore >= 15) {
    reasons.push(`Close to your location`);
  }
  
  // 4. Age-based reasons (enhanced with more coverage)
  if (scores.ageScore > 0) {
    const childAges = userProfile.children?.map(c => getChildAge(c)).filter(age => age !== null) || [];
    const campMin = parseInt(camp.age_min) || 0;
    const campMax = parseInt(camp.age_max) || 18;
    
    if (childAges.length > 0 && (campMin > 0 || campMax < 18)) {
      if (scores.ageScore >= 4) {
        // Perfect or close age match
        if (childAges.length === 1) {
          reasons.push(`Perfect for your ${childAges[0]}-year-old`);
        } else {
          const ageText = childAges.length === 2 
            ? `${childAges[0]} and ${childAges[1]} year olds`
            : `${childAges.slice(0, -1).join(', ')}, and ${childAges[childAges.length - 1]} year olds`;
          reasons.push(`Perfect for your ${ageText}`);
        }
      } else if (scores.ageScore >= 2) {
        // Good age match
        if (childAges.length === 1) {
          reasons.push(`Great for your ${childAges[0]}-year-old (camp ages ${campMin}-${campMax})`);
        }
      }
    }
  }
  
  // 5. Schedule match information
  if (scores.schedule && typeof scores.schedule === 'object') {
    const scheduleScore = scores.schedule.score || 0;
    const scheduleReason = scores.schedule.reason;
    
    // Only add schedule reasons if they exist and are not null/empty
    // Skip "No schedule data available" messages
    if (scheduleScore >= 15) {
      reasons.push(`Schedule matches your preferred times`);
    } else if (scheduleScore >= 10 && scheduleReason && scheduleReason.trim() && scheduleReason !== 'No schedule data available') {
      reasons.push(scheduleReason);
    }
  }
  
  // Note: Rating bonus is still calculated and used in scoring (see scores.ratingBonus above),
  // but we don't display it as a match reason on the card per user request
  
  // Limit to 4-5 most relevant reasons, prioritize by order added
  const prioritizedReasons = reasons.slice(0, 5);
  
  // Default reason if no specific matches
  if (prioritizedReasons.length === 0) {
    prioritizedReasons.push(`Highly-rated program in your area`);
  }
  
  // Return both array and joined string for backward compatibility
  return {
    reasons: prioritizedReasons,
    reason: prioritizedReasons.join(' • ')
  };
}

/**
 * Calculate confidence level based on total score
 * Returns user-friendly labels for match quality
 */
function getConfidenceLevel(totalScore) {
  if (totalScore >= 80) return 'excellent match';
  if (totalScore >= 60) return 'great match';
  if (totalScore >= 40) return 'good match';
  return 'low'; // For internal use, but won't be shown since minScore is 40
}

/**
 * Main function to find perfect matches for a user
 */
export async function findPerfectMatches(userProfile, availableCamps, options = {}) {
  const {
    maxResults = 12,
    minScore = 30,
    userSearchLocation = null,
    useCache = true
  } = options;
  
  if (!userProfile || !availableCamps || availableCamps.length === 0) {
    return [];
  }
  
  // Create cache key
  const cacheKey = `perfect_matches_${userProfile.id}_${availableCamps.length}_${JSON.stringify(userProfile.children?.map(c => getChildAge(c)))}_${userSearchLocation?.latitude || 'null'}`;
  
  // Try cache first
  if (useCache) {
    try {
      const cachedMatches = getFromCache(cacheKey);
      if (cachedMatches) {
        return cachedMatches;
      }
    } catch (error) {
      console.warn('Cache read failed, proceeding with fresh calculation:', error);
    }
  }
  
  // Calculate matches
  const matches = [];
  
  availableCamps.forEach((camp, index) => {
    const verbose = false;
    
    // Normalize camp location - handle both nested location object and direct coordinates
    let campLocation = camp.location;
    if (!campLocation || (!campLocation.lat && !campLocation.latitude)) {
      // Try to get from direct camp properties
      const lat = camp.latitude ?? camp.lat;
      const lng = camp.longitude ?? camp.lng;
      if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
        campLocation = { lat, lng };
      } else {
        // If still no valid location, create a placeholder that will return default score
        campLocation = { lat: null, lng: null };
      }
    }
    
    // Get relevant preferences for this camp type
    const relevantPreferences = getRelevantPreferences(userProfile, camp);
    
    const ageResult = calculateAgeScore(camp.age_min, camp.age_max, userProfile.children, verbose);
    const ageScore = typeof ageResult === 'object' ? ageResult.score : ageResult;
    const interestResult = calculateInterestScore(camp, userProfile.children, verbose);
    const interestScore = interestResult.score || 0;
    const distanceScore = calculateDistanceScore(campLocation, userProfile.locations, userSearchLocation, relevantPreferences, verbose);
    
    // Enhanced schedule scoring (returns object with score, reason, confidence)
    const scheduleResult = calculateScheduleScore(camp, relevantPreferences, verbose);
    const scheduleScore = typeof scheduleResult === 'object' ? scheduleResult.score : scheduleResult;
    
    // Price scoring removed - not used in matching (most camps don't have cost data)
    // (calculatePriceScore function kept for potential future use)
    
    // Date availability scoring removed - not used in matching
    // (calculateDateScore function kept for potential future use)
    
    // Rating bonus (0-10 points) to prioritize highly-rated camps
    // Only applies to camps with 30+ ratings to ensure reliability
    let ratingBonus = 0;
    const rating = parseFloat(camp.rating);
    const ratingCount = camp.user_ratings_total || camp.review_count || camp.reviewCount || 0;

    if (!isNaN(rating) && rating > 0 && ratingCount >= 30) {
      // Add 0-10 points based on rating (3.0-5.0 stars)
      ratingBonus = Math.min(10, (rating - 3.0) * 5);
      ratingBonus = Math.max(0, ratingBonus);
    }
    
    // Combine per-child data from age and interest scores
    const matchedChildren = [];
    const childAgeScores = typeof ageResult === 'object' ? ageResult.childAgeScores : [];
    const interestMatchedChildren = interestResult.matchedChildren || [];
    
    // Create a map of children by ID/name
    const childrenMap = new Map();
    userProfile.children?.forEach(child => {
      const key = child.id || child.name;
      if (key) {
        childrenMap.set(key, child);
      }
    });
    
    // Combine age and interest data for each child
    const processedChildIds = new Set();
    
    // Process children from interest matches (only include those with meaningful matches)
    interestMatchedChildren.forEach(interestChild => {
      // Only include children with interest score > 0 (actual interest matches)
      // Include if they have:
      // 1. Specific interest matches (exactMatchCount > 0 OR relatedMatchCount > 0), OR
      // 2. Legitimate category match (categoryMatch:true via explicit PREFERRED_TYPE_TO_CATEGORY_MAP)
      // Since we removed substring fallback matching, categoryMatch:true now indicates a legitimate match
      const hasSpecificInterests = (interestChild.exactMatchCount || 0) > 0 || (interestChild.relatedMatchCount || 0) > 0;
      const hasLegitimateCategoryMatch = interestChild.categoryMatch === true;
      
      if (interestChild.score > 0 && (hasSpecificInterests || hasLegitimateCategoryMatch)) {
        const childId = interestChild.childId;
        const child = childrenMap.get(childId);
        if (child) {
          const ageData = childAgeScores.find(a => (a.childId === childId));
          const totalScore = interestChild.score + (ageData?.ageScore || 0);
          
          // Only add if total score meets minimum threshold (meaningful match)
          // AND has either specific interest matches OR legitimate category match
          // This ensures children are only tagged when they have actual alignment, but allows category matches
          // since category matching now uses explicit mapping only (no false positives)
          if (totalScore >= 5 && (hasSpecificInterests || hasLegitimateCategoryMatch)) {
            // Try multiple possible property names for photo URL
            const photoUrl = child.photo_url || child.photoUrl || child.profile_photo_url || child.profilePhotoUrl || null;
            
            // Debug logging for child photo
            if (!photoUrl && child) {
              console.log(`⚠️ [Match] Child "${child.name}" has no photo URL. Available properties:`, Object.keys(child).filter(k => k.toLowerCase().includes('photo')));
            }
            
            matchedChildren.push({
              childId: child.id || child.name,
              childName: child.name,
              photoUrl: photoUrl,
              photo_url: photoUrl, // Also include snake_case for compatibility
              interestScore: interestChild.score,
              ageScore: ageData?.ageScore || 0,
              totalScore: totalScore,
              matchedInterests: interestChild.matchedInterests || [],
              exactMatchCount: interestChild.exactMatchCount || 0,
              relatedMatchCount: interestChild.relatedMatchCount || 0,
              hasExactMatch: interestChild.hasExactMatch || false,
              categoryMatch: interestChild.categoryMatch || false,
              ageMatch: ageData?.ageMatch || false,
              childAge: ageData?.age || null
            });
            processedChildIds.add(childId);
          }
        }
      }
    });
    
    // REMOVED age-only matching - children must have specific interest matches to be included
    // Age-only matches were causing false positives where children without interest alignment
    // were being tagged on matches. We now require actual interest alignment (exact or related matches)
    // Age scores are still used to boost total scores, but age alone is not sufficient for inclusion.
    
    // Sort by total score (highest first)
    matchedChildren.sort((a, b) => b.totalScore - a.totalScore);
    
    // Final filter: only keep children with totalScore >= 5 (meaningful contribution)
    // This ensures we only show children who actually contributed significantly to the match
    const filteredMatchedChildren = matchedChildren.filter(child => child.totalScore >= 5);
    
    // Use filtered list
    matchedChildren.length = 0;
    matchedChildren.push(...filteredMatchedChildren);
    
    const scores = {
      ageScore,
      interestScore,
      interest: interestResult, // Store full interest result for match reasons
      distanceScore,
      scheduleScore,
      schedule: scheduleResult, // Store full schedule result
      ratingBonus // Include ratingBonus for match reasons
    };
    
    // Calculate base score (normalize schedule score if it's an object)
    const baseScore = scores.ageScore + scores.interestScore + scores.distanceScore + 
                     (typeof scores.scheduleScore === 'number' ? scores.scheduleScore : scores.scheduleScore?.score || 0) +
                     ratingBonus;
    
    // Build available max: 5 (age) + 30 (interest) + 20 (distance) + 20 (schedule) + 10 (rating) = 85
    // This accounts for all factors being included in baseScore
    const availableMax = 85;

    // Apply final score calculation with community data boosts and dynamic max
    const totalScore = calculateFinalScore(baseScore, camp, availableMax);
    const roundedScore = Math.round(totalScore);
    
    const detectedCampType = getCampType(camp);
    
    if (totalScore >= minScore) {
      const matchReasonData = generateMatchReason(camp, userProfile, scores);
      
      matches.push({
        camp: camp,
        score: roundedScore,
        confidence: getConfidenceLevel(totalScore),
        reason: matchReasonData.reason,
        reasons: matchReasonData.reasons,
        matchedChildren: matchedChildren, // Per-child match data
        scores: {
          ...scores,
          ratingBonus: ratingBonus
        }
      });
    }
  });
  
  // Sort by interest matches first, then by score (highest first) and limit results
  const sortedMatches = matches
    .sort((a, b) => {
      // Priority 1: Interest matches first (exact or related matches)
      const aHasInterests = (a.scores?.interest?.hasExactMatch) || 
                           (a.scores?.interest?.matchedInterests?.length > 0);
      const bHasInterests = (b.scores?.interest?.hasExactMatch) || 
                           (b.scores?.interest?.matchedInterests?.length > 0);
      
      if (aHasInterests && !bHasInterests) return -1; // a comes first
      if (!aHasInterests && bHasInterests) return 1;  // b comes first
      
      // Priority 2: If both have interests or neither has interests, sort by score
      return b.score - a.score;
    })
    .slice(0, maxResults);
  
  // Cache results
  if (useCache && sortedMatches.length > 0) {
    try {
      await cachedAPICall(
        cacheKey,
        () => sortedMatches,
        CACHE_TTL.MEDIUM,
        true
      );
    } catch (error) {
      console.warn('Cache write failed:', error);
    }
  }
  
  return sortedMatches;
}

/**
 * Get camps within radius for matching
 */
export async function getCampsForMatching(userProfile, radiusMiles = 25) {
  // This would typically fetch from your camp database
  // For now, we'll return a placeholder that the calling code should replace
  // with actual camp data from your database
  
  console.log(`Getting camps within ${radiusMiles} miles for user ${userProfile.id}`);
  
  // Return empty array - the calling code should populate this with actual camp data
  return [];
}

/**
 * Get match statistics for a user
 */
export function getMatchStats(matches) {
  if (!matches || matches.length === 0) {
    return {
      total: 0,
      highConfidence: 0,
      mediumConfidence: 0,
      lowConfidence: 0,
      averageScore: 0
    };
  }
  
  const stats = {
    total: matches.length,
    highConfidence: matches.filter(m => m.confidence === 'high').length,
    mediumConfidence: matches.filter(m => m.confidence === 'medium').length,
    lowConfidence: matches.filter(m => m.confidence === 'low').length,
    averageScore: Math.round(matches.reduce((sum, m) => sum + m.score, 0) / matches.length)
  };
  
  return stats;
}
